import React, { useState, useEffect, useCallback } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  AppBar,
  Tabs,
  Tab
} from '@mui/material'
import ResidentialPlanDialogActions from './ResidentialPlanDialogActions'

//Components
import YourRelationshipSection from './YourRelationshipSection/YourRelationshipSection'
import { IARDialog } from '../../Shared/Dialog/IARDialog'
import './ResidentialPlansDetailDialog.scss'
import EmergencyContactSection from './EmergencyContactSection/EmergencyContactSection'
import WhoOwnsPropertySection from './WhoOwnsPropertySection/WhoOwnsPropertySection'
import WhoLivesHereSection from './WhoLivesHere/WhoLivesHereSection'
import PetsSection from './Pets/PetsSection'
import AdditionalInformation from './AdditionalInformation/AdditionalInformation'
import BuildingAndProperty from './BuildingAndProperty/BuildingAndProperty'
import { useCMSContext } from '../../CMS/CMSContext'
import { objectHasAnyValue } from '../../../../resources/Utils'
import AttachmentsSection from './Attachments/attachments'

const ResidentialPlansDetailDialog = React.memo(
  ({
    id,
    title,
    open,
    onCancel,
    onClose,
    page,
    pageChange,
    setResidentialPlansEntity,
    residentialplans
  }) => {
    const cmsContext = useCMSContext()
    const { iarMember, isReadOnlyMember } = cmsContext.state
    const iarMemberFullName = iarMember?.fullName

    const [value, setValue] = useState(0)
    const [localId, setLocalId] = useState(id)
    const [residentialPlanEntity, setResidentialPlanEntity] = useState({})
    const [deleteDialog, setDeleteDialog] = useState({ open: false })
    const [approveDialog, setApproveDialog] = useState({ open: false })
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
      if (localId) {
        async function fetchDetail() {
          const response = await ResidentialPlanDialogActions.getResidentialPlanDetail(
            id,
            iarMemberFullName
          )
          if (response) {
            setResidentialPlanEntity(response)
          }
        }
        fetchDetail()
      } else {
        setResidentialPlanEntity({})
      }
    }, [localId, id, iarMemberFullName])

    useEffect(() => {
      setIsReady(true)
    }, [])

    const a11yProps = (index) => {
      return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
      }
    }

    const handleClose = (dataPosted = false) => {
      setLocalId('')
      onCancel(dataPosted)
    }

    const openConfirmationDialog = () => {
      setDeleteDialog({ open: true })
    }

    const preDelete = () => {
      if (isReady) {
        deleteRecord()
      }
      setIsReady(false)
    }

    const cancelApproveDialog = () => setApproveDialog({ open: false })

    const openApproveDialog = () => setApproveDialog({ open: true })

    const approveRecord = useCallback(() => {
      async function fetchApproveResidentialPlan() {
        let idToAprove = []
        idToAprove.push(localId)
        const response = await ResidentialPlanDialogActions.approveResidentialPlan(
          idToAprove,
          iarMemberFullName
        )
        setApproveDialog({ open: false })
        onClose()
        if (response) {
          pageChange(null, page)
        }
      }
      fetchApproveResidentialPlan()
    }, [iarMemberFullName, localId, onClose, page, pageChange])

    const deleteRecord = useCallback(() => {
      async function fetchResidentialPlansDelete() {
        const response = await ResidentialPlanDialogActions.deleteResidentialPlan(
          id,
          iarMemberFullName,
          setResidentialPlansEntity,
          residentialplans
        )
        setDeleteDialog({ open: false })
        onClose()
        if (response) {
          pageChange(null, page)
        }
      }
      fetchResidentialPlansDelete()
    }, [iarMemberFullName, id, onClose, page, pageChange, residentialplans, setResidentialPlansEntity])

    const cancelDialog = () => setDeleteDialog({ open: false })

    const handleTabChange = (event, newValue) => {
      setValue(newValue)
    }

    function TabPanel(props) {
      const { children, value, index, ...other } = props

      return (
        <div
          role="tabpanel"
          hidden={value !== index}
          className="section-container"
          id={`simple-tabpanel-${index}`}
          aria-labelledby={`simple-tab-${index}`}
          {...other}
        >
          {value === index && <div className="section-content">{children}</div>}
        </div>
      )
    }

    const hasBuildingAndProperty = React.useMemo(() => {
      return objectHasAnyValue(residentialPlanEntity.buildingAndProperty)
    }, [residentialPlanEntity.buildingAndProperty])

    return (
      <div>
        <IARDialog
          open={deleteDialog.open}
          title="Confirm"
          content={
            <div className="dialogContent">
              <div>Do you want to delete this residential plan?</div>
            </div>
          }
          onOk={preDelete}
          onCancel={cancelDialog}
        />
        <IARDialog
          open={approveDialog.open}
          title="Confirm"
          content={
            <div className="dialogContent">
              <div>Do you want to approve this residential plan?</div>
            </div>
          }
          onOk={approveRecord}
          onCancel={cancelApproveDialog}
        />
        <Dialog
          maxWidth="md"
          className="residential-plans-detail-dialog"
          disableBackdropClick
          disableEscapeKeyDown
          open={open}
        >
          <DialogTitle className="detail-dialog-title">
            <span>{title}</span>
            <IconButton aria-label="close" className="close-button" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="detail-dialog-content">
            <Grid container>
              <AppBar elevation={0} position="sticky" className="header">
                <Tabs
                  variant="fullWidth"
                  border={0}
                  className="detail-tabs-container"
                  value={value}
                  onChange={handleTabChange}
                  aria-label="simple tabs example"
                >
                  <Tab className="detail-tab" label="Property overview" {...a11yProps(0)} />
                  {residentialPlanEntity.emergencyContacts &&
                  residentialPlanEntity.emergencyContacts.length > 0 ? (
                    <Tab className="detail-tab" label="Contacts" {...a11yProps(1)} />
                  ) : (
                    <div></div>
                  )}
                  {residentialPlanEntity.whoLivesHereIds &&
                  (residentialPlanEntity.whoLivesHereIds.numberOfAdults > 0 ||
                    residentialPlanEntity.whoLivesHereIds.numberOfChildren > 0) ? (
                    <Tab className="detail-tab" label="Who lives here?" {...a11yProps(2)} />
                  ) : (
                    <div></div>
                  )}
                  {residentialPlanEntity.petIds &&
                  (residentialPlanEntity.petIds.indoorPets > 0 ||
                    residentialPlanEntity.petIds.outdoorPets > 0) ? (
                    <Tab className="detail-tab" label="Pet or Service Animal?" {...a11yProps(3)} />
                  ) : (
                    <div></div>
                  )}
                  {hasBuildingAndProperty &&
                  residentialPlanEntity.buildingAndProperty &&
                  (residentialPlanEntity.buildingAndProperty.buildingInformation ||
                    residentialPlanEntity.buildingAndProperty.fireAlarmSystem ||
                    residentialPlanEntity.buildingAndProperty.hazardousMaterials ||
                    residentialPlanEntity.buildingAndProperty.propertyAccess ||
                    residentialPlanEntity.buildingAndProperty.roofing ||
                    residentialPlanEntity.buildingAndProperty.utilities ||
                    residentialPlanEntity.buildingAndProperty.waterSupply) ? (
                    <Tab className="detail-tab" label="Building and property" {...a11yProps(4)} />
                  ) : (
                    <div></div>
                  )}

                  {residentialPlanEntity.additionalInformation &&
                  residentialPlanEntity.additionalInformation.hasAdditionalInformation ? (
                    <Tab className="detail-tab" label="Additional information" {...a11yProps(5)} />
                  ) : (
                    <div></div>
                  )}

                  {residentialPlanEntity.images && residentialPlanEntity.images.length > 0 ? (
                    <Tab className="detail-tab" label="Images" {...a11yProps(6)} />
                  ) : (
                    <div></div>
                  )}
                </Tabs>
              </AppBar>

              <TabPanel value={value} index={0}>
                <YourRelationshipSection residentialPlanEntity={residentialPlanEntity} />
              </TabPanel>
              {(residentialPlanEntity.emergencyContacts &&
                residentialPlanEntity.emergencyContacts.length > 0) ||
              (residentialPlanEntity.whoOwnsProperty &&
                residentialPlanEntity.whoOwnsProperty.length > 0) ? (
                <TabPanel value={value} index={1}>
                  <h2 className="contacts-title">Contacts</h2>
                  {residentialPlanEntity.emergencyContacts &&
                  residentialPlanEntity.emergencyContacts.length > 0 ? (
                    <EmergencyContactSection
                      emergencyContactEntity={residentialPlanEntity.emergencyContacts}
                    />
                  ) : null}
                  {residentialPlanEntity.whoOwnsProperty &&
                  residentialPlanEntity.whoOwnsProperty.length > 0 ? (
                    <WhoOwnsPropertySection
                      whoOwnsPropertyEntity={residentialPlanEntity.whoOwnsProperty}
                    />
                  ) : null}
                </TabPanel>
              ) : null}
              {residentialPlanEntity.whoLivesHereIds &&
              (residentialPlanEntity.whoLivesHereIds.numberOfAdults > 0 ||
                residentialPlanEntity.whoLivesHereIds.numberOfChildren > 0) ? (
                <TabPanel value={value} index={2}>
                  <WhoLivesHereSection residentialplansEntity={residentialPlanEntity} />
                </TabPanel>
              ) : null}

              {residentialPlanEntity.petIds &&
              (residentialPlanEntity.petIds.indoorPets > 0 ||
                residentialPlanEntity.petIds.outdoorPets > 0) ? (
                <TabPanel value={value} index={3}>
                  <PetsSection residentialplansEntity={residentialPlanEntity} />
                </TabPanel>
              ) : null}
              {residentialPlanEntity.buildingAndProperty &&
              (residentialPlanEntity.buildingAndProperty.buildingInformation ||
                residentialPlanEntity.buildingAndProperty.fireAlarmSystem ||
                residentialPlanEntity.buildingAndProperty.hazardousMaterials ||
                residentialPlanEntity.buildingAndProperty.propertyAccess ||
                residentialPlanEntity.buildingAndProperty.roofing ||
                residentialPlanEntity.buildingAndProperty.utilities ||
                residentialPlanEntity.buildingAndProperty.waterSupply) ? (
                <TabPanel value={value} index={4}>
                  <BuildingAndProperty
                    buildingAndPropertyEntity={residentialPlanEntity.buildingAndProperty}
                  />
                </TabPanel>
              ) : null}

              {residentialPlanEntity.additionalInformation &&
              residentialPlanEntity.additionalInformation.hasAdditionalInformation ? (
                <TabPanel value={value} index={5}>
                  <AdditionalInformation
                    additionalInformationEntity={residentialPlanEntity.additionalInformation}
                  />
                </TabPanel>
              ) : null}

              {residentialPlanEntity.images && residentialPlanEntity.images.length > 0 ? (
                <TabPanel value={value} index={6}>
                  <AttachmentsSection imagesList={residentialPlanEntity.images} />
                </TabPanel>
              ) : null}
            </Grid>

            {iarMember?.isResidentialPlansApproved ? (
              <>
                <Button
                  variant="contained"
                  id="deleteResidentialPlan"
                  size="medium"
                  className={
                    isReadOnlyMember ? 'deleteResidentialPlanDisabled' : 'deleteResidentialPlan'
                  }
                  disabled={isReadOnlyMember}
                  onClick={openConfirmationDialog}
                >
                  Delete residential plan
                </Button>

                {!residentialPlanEntity.approved ? (
                  <Button
                    variant="contained"
                    id="deleteResidentialPlan"
                    size="medium"
                    className={
                      isReadOnlyMember ? 'approveResidentialPlanDisabled' : 'approveResidentialPlan'
                    }
                    disabled={isReadOnlyMember}
                    onClick={openApproveDialog}
                  >
                    Approve residential plan
                  </Button>
                ) : null}
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    )
  }
)

export default ResidentialPlansDetailDialog
